import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ROLES, ROLE_LABEL, ROLE_BADGE_CLS, userInitials } from "../../lib/roleOptions";
import { roleBreakdown, breakdownLabel, FIELD_ROLES } from "../../lib/population";
import { joinParts, plural } from "../../lib/vocabulary";
import TerritoryLabel from "../../components/common/TerritoryLabel";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Search, Users2, Mail, Phone, Send, UserCog, Inbox, Archive, RotateCcw,
  UserPlus, ArrowRightLeft, UserMinus, Hourglass,
} from "lucide-react";
import { toast } from "sonner";
import {
  isPendingMasterChange, sentForApprovalMessage, pendingMasterLabel,
} from "../../lib/masterChange";
import MasterChangeDestination from "../../components/common/MasterChangeDestination";
import UserEditDialog from "./UserEditDialog";
import InviteCreateDialog from "./InviteCreateDialog";
import InvitesPanel from "./InvitesPanel";
import { InductDialog, ReassignDialog, ReleaseDialog } from "./TeamChangeDialogs";
import StickyHeading from "../../components/common/StickyHeading";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../../components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const ALL = "__all__";

export default function TeamPage() {
  const { user: viewer } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [editTarget, setEditTarget] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);
  // DEACTIVATE FILES TOO, AND HAD NO DIALOG AT ALL. It is one of the presses in
  // the 24 August report: the button answered 200, the card did not change, and
  // nothing anywhere said a request had been created. A control that FILES has
  // to say so before it acts, so this one gets the same confirmation the
  // archive has.
  const [toggleTarget, setToggleTarget] = useState(null);
  const [toggling, setToggling] = useState(false);
  // #15 — team changes.
  const [pendingChanges, setPendingChanges] = useState([]);
  // ARCHIVE / RESTORE / EDIT ARE MASTER CHANGES, NOT TEAM CHANGES (D-006), and
  // the badge above only ever read `/team-changes`. So a filed archive left no
  // mark on this page at all: the toast said "Sent for approval", the row
  // stayed exactly where it was, and the next reader saw a 200 that had
  // apparently done nothing. It had done something — in a collection this page
  // never fetched.
  const [pendingMasterChanges, setPendingMasterChanges] = useState([]);
  const [inductOpen, setInductOpen] = useState(false);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [releaseTarget, setReleaseTarget] = useState(null);

  // Account administration (invite, edit, archive) stays with admin/director.
  // Team management (induct/reassign/release) belongs to all four roles — a
  // Sales Manager within their team, the others company-wide (D2.5).
  const canAdminister = viewer?.role === "admin" || viewer?.role === "director";
  const canManageTeam = ["assistant_manager", "admin_manager", "admin", "director"].includes(viewer?.role);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const [{ data }, changes, masterChanges] = await Promise.all([
        api.get("/users", { params: showArchived ? { include_archived: true } : {} }),
        canManageTeam
          ? api.get("/team-changes", { params: { status: "Submitted" } }).then((r) => r.data)
          : Promise.resolve([]),
        // Only the roles that file them can see them; for everybody else the
        // endpoint returns their own filings, which is nothing on this page.
        canAdminister
          ? api.get("/master-changes").then((r) => r.data)
          : Promise.resolve([]),
      ]);
      setUsers(data);
      setPendingChanges(changes);
      setPendingMasterChanges(masterChanges);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  // One pending change per officer is a backend rule, so a map keyed by the
  // officer is total.
  const pendingBySoId = useMemo(() => {
    const m = {};
    for (const c of pendingChanges) m[c.so_id] = c;
    return m;
  }, [pendingChanges]);

  // Filed-and-waiting user master changes, by the user they act on. Filtered
  // here rather than server-side so this stays one request: `/master-changes`
  // carries every master and every decided row.
  const pendingMasterByUserId = useMemo(() => {
    const m = {};
    for (const c of pendingMasterChanges) {
      if (c.master === "users" && c.status === "Submitted") m[c.target_id] = c;
    }
    return m;
  }, [pendingMasterChanges]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== ALL && u.role !== roleFilter) return false;
      if (statusFilter === "active" && !u.is_active) return false;
      if (statusFilter === "inactive" && u.is_active) return false;
      if (s) {
        const hay = `${u.full_name || ""} ${u.email || ""} ${u.territory || ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [users, search, roleFilter, statusFilter]);

  // The role breakdown moved to `lib/population.roleBreakdown` — this page was
  // its promotion source and now imports it, so the ten surfaces that count a
  // population count it the same way. The one behavioural change: a role this
  // build does not know is counted as `unknown` and named, where the old
  // `else if` chain dropped it and let the parts sum to less than the total.
  const counts = useMemo(() => roleBreakdown(users), [users]);

  // B6: A TEAM INCLUDES ITS MANAGER, so an inclusive count must SHOW ITS SPLIT
  // rather than assert a total the reader will parse as "my reports". The
  // viewer is in this roster whenever the backend's scope put them there.
  //
  // `teamCountLabel` is deliberately NOT used: its wording is "N reports + you",
  // which is true for a Sales Manager and false for the Director and the Admin
  // Manager, who see people who do not report to them.
  const viewerInRoster = useMemo(
    () => !!viewer?.id && users.some((u) => u.id === viewer.id),
    [users, viewer]
  );
  const rosterCountLabel = !viewerInRoster
    ? plural(counts.total, "user")
    : counts.total <= 1
    ? "just you"
    : `${plural(counts.total - 1, "other user")} + you`;

  const managers = useMemo(
    () => users.filter((u) => ["director", "admin", "assistant_manager", "admin_manager"].includes(u.role) && u.is_active && u.archived !== true),
    [users]
  );

  const usersById = useMemo(() => {
    const m = {};
    for (const u of users) m[u.id] = u;
    return m;
  }, [users]);

  async function handleSave(userId, patch) {
    try {
      const { data } = await api.patch(`/users/${userId}`, patch);
      toast.success(isPendingMasterChange(data) ? sentForApprovalMessage(data) : "User updated");
      setEditTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function handleArchive() {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      const { data } = await api.post(`/users/${archiveTarget.id}/archive`);
      toast.success(isPendingMasterChange(data) ? sentForApprovalMessage(data) : "User archived");
      setArchiveTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setArchiving(false);
    }
  }

  async function handleRestore(u) {
    try {
      const { data } = await api.post(`/users/${u.id}/restore`);
      toast.success(isPendingMasterChange(data) ? sentForApprovalMessage(data) : "User restored");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function handleToggleActive() {
    const u = toggleTarget;
    if (!u) return;
    setToggling(true);
    try {
      const { data } = await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(
        isPendingMasterChange(data)
          ? sentForApprovalMessage(data)
          : (u.is_active ? "User deactivated" : "User reactivated")
      );
      setToggleTarget(null);
      load();
    } catch (e) {
      // A 409 here is the duplicate guard, and its message names the request
      // already waiting, who holds it and how to withdraw it. Shown as-is.
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setToggling(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      <StickyHeading
        icon={Users2}
        label="Team"
        title="Manage your field force"
        subtitle={
          <span data-testid="team-count-summary">
            {joinParts([
              rosterCountLabel,
              `${counts.active} active`,
              breakdownLabel(counts),
            ])}
          </span>
        }
        testId="team-heading"
        actions={
          <div className="flex items-center gap-2">
            {canManageTeam && (
              <Button
                variant="outline"
                onClick={() => setInductOpen(true)}
                className="h-11 border-callisto text-callisto-navy font-semibold"
                data-testid="team-induct-button"
              >
                <UserPlus size={14} className="mr-1.5" />
                Induct member
              </Button>
            )}
            {canAdminister && (
              <Button
                onClick={() => setInviteOpen(true)}
                className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white font-semibold"
                data-testid="team-invite-button"
              >
                <Send size={14} className="mr-1.5" />
                Invite user
              </Button>
            )}
          </div>
        }
      />

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="team-error">
          {error}
        </div>
      )}

      <Tabs defaultValue="users" className="space-y-4">
        {/* Invites are account administration, so the tab bar only appears for
            admin/director — a single always-on Users tab would be noise. */}
        {canAdminister && (
        <TabsList className="h-11 p-1 bg-white border border-callisto rounded-lg" data-testid="team-tabs-list">
          <TabsTrigger
            value="users"
            className="h-9 px-4 data-[state=active]:bg-callisto-navy data-[state=active]:text-callisto-gold"
            data-testid="team-tab-users"
          >
            <Users2 size={14} className="mr-1.5" />
            Users
          </TabsTrigger>
          <TabsTrigger
            value="invites"
            className="h-9 px-4 data-[state=active]:bg-callisto-navy data-[state=active]:text-callisto-gold"
            data-testid="team-tab-invites"
          >
            <Mail size={14} className="mr-1.5" />
            Pending invites
          </TabsTrigger>
        </TabsList>
        )}

        <TabsContent value="users" className="space-y-4">
          <div className="bg-white border border-callisto rounded-xl p-3 md:p-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="relative sm:col-span-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-callisto-muted" />
              <Input
                placeholder="Search name, email, territory…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-11 pl-9 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="team-search-input"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="team-filter-role">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All roles</SelectItem>
                {ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-11 focus:ring-callisto-gold" data-testid="team-filter-status">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Active + Inactive</SelectItem>
                <SelectItem value="active">Active only</SelectItem>
                <SelectItem value="inactive">Inactive only</SelectItem>
              </SelectContent>
            </Select>
            {canAdminister && (
              <label className="sm:col-span-3 inline-flex items-center gap-2 text-xs text-callisto-body cursor-pointer">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  className="h-4 w-4 rounded border-callisto accent-callisto-navy"
                  data-testid="team-show-archived"
                />
                Show archived users (departed staff)
              </label>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-callisto rounded-xl p-10 flex flex-col items-center text-center">
              <div className="h-14 w-14 rounded-xl bg-callisto-navy/5 flex items-center justify-center mb-3">
                <Inbox className="h-6 w-6 text-callisto-navy" />
              </div>
              <p className="text-sm font-semibold text-callisto-navy">No users match your filters</p>
              <p className="mt-1 text-xs text-callisto-muted">Try widening the search or clearing the filters above.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="team-users-grid">
              {filtered.map((u) => (
                <UserCard
                  key={u.id}
                  user={u}
                  manager={u.manager_id ? usersById[u.manager_id] : null}
                  canAdminister={canAdminister}
                  canManageTeam={canManageTeam}
                  viewerId={viewer?.id}
                  pendingChange={pendingBySoId[u.id]}
                  onEdit={() => setEditTarget(u)}
                  onToggleActive={() => setToggleTarget(u)}
                  pendingMaster={pendingMasterByUserId[u.id]}
                  onArchive={() => setArchiveTarget(u)}
                  onRestore={() => handleRestore(u)}
                  onReassign={() => setReassignTarget(u)}
                  onRelease={() => setReleaseTarget(u)}
                />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="invites">
          <InvitesPanel onChanged={load} />
        </TabsContent>
      </Tabs>

      <UserEditDialog
        open={!!editTarget}
        user={editTarget}
        managers={managers}
        onClose={() => setEditTarget(null)}
        onSave={handleSave}
      />
      <InviteCreateDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />
      <InductDialog
        open={inductOpen}
        viewer={viewer}
        onClose={() => setInductOpen(false)}
        onDone={load}
      />
      <ReassignDialog
        open={!!reassignTarget}
        officer={reassignTarget}
        onClose={() => setReassignTarget(null)}
        onDone={load}
      />
      <ReleaseDialog
        open={!!releaseTarget}
        officer={releaseTarget}
        onClose={() => setReleaseTarget(null)}
        onDone={load}
      />

      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send this archive for approval?</AlertDialogTitle>
            <AlertDialogDescription>
              {/* THE DESTINATION, FIRST AND IN THE FUTURE TENSE. This dialog
                  used to open with "will be treated as departed … their sign-in
                  is revoked" — an end state, in the present tense, for an
                  endpoint that files a request. All eleven duplicate presses of
                  24 August happened because of that sentence. */}
              <MasterChangeDestination testId="team-archive-destination" />
              <span className="block mt-2">
                Nothing changes yet. Once approved,{" "}
                <span className="font-semibold text-callisto-navy">{archiveTarget?.full_name}</span>{" "}
                will be treated as departed: hidden from the Team list and from every assignee and
                manager picker, and their sign-in revoked. All of their historical records — visits,
                expenses, leaves, attendance — are retained. Tick “Show archived users” to find and
                restore them. Until then the card says who is holding the request.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleArchive(); }}
              disabled={archiving}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="team-archive-confirm"
            >
              Send for approval
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deactivate / reactivate files a `users` update master change, exactly
          as Edit and Archive do. It had no dialog, so it was the one press on
          this page with no way at all to learn where it had gone. */}
      <AlertDialog open={!!toggleTarget} onOpenChange={(o) => !o && setToggleTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Send this {toggleTarget?.is_active ? "deactivation" : "reactivation"} for approval?
            </AlertDialogTitle>
            <AlertDialogDescription>
              <MasterChangeDestination testId="team-toggle-destination" />
              <span className="block mt-2">
                Nothing changes yet. Once approved,{" "}
                <span className="font-semibold text-callisto-navy">{toggleTarget?.full_name}</span>{" "}
                {toggleTarget?.is_active
                  ? "will not be able to sign in, and will stay visible in Team so their book stays attributable."
                  : "will be able to sign in again."}{" "}
                Until then the card says who is holding the request.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={toggling}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleToggleActive(); }}
              disabled={toggling}
              className="bg-callisto-navy hover:bg-callisto-navy/90"
              data-testid="team-toggle-confirm"
            >
              Send for approval
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const PENDING_LABEL = { induct: "Induct", reassign: "Reassign", release: "Release" };

function UserCard({
  user, manager, canAdminister, canManageTeam, viewerId, pendingChange,
  pendingMaster, onEdit, onToggleActive, onArchive, onRestore, onReassign,
  onRelease,
}) {
  // Induct / reassign / release apply to Sales Officers. An assigned officer
  // gets Reassign + Release; an unassigned one is picked up through the Induct
  // dialog, so their card only marks the state.
  const isAssignedSo = user.role === "sales_officer" && !!user.manager_id;
  const isUnassignedSo = user.role === "sales_officer" && !user.manager_id;
  const showTeamActions =
    canManageTeam && isAssignedSo && !user.archived && user.is_active && !pendingChange;
  // #126 CLOSED THESE BUTTONS WHILE ANY MASTER CHANGE ON THIS USER WAS PENDING.
  // That is gone, and deliberately. The refusal is server-side now
  // (`file_master_change`), where it applies to every caller and cannot race the
  // fetch that populates this list — and its equivalence rule is narrower than a
  // disabled button can express: two DIFFERENT edits to one user are two
  // different requests, and the card had no way to know which edit was about to
  // be typed. A button disabled here would forbid what the endpoint accepts, and
  // a second copy of "the same change" living in the JSX is the shape this repo
  // keeps paying for. What the card owes the reader is what is already filed and
  // who holds it — below — not a guess about what they are about to file.

  return (
    <div
      className={`bg-white border rounded-xl p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${
        user.archived ? "border-orange-200 opacity-70" : user.is_active ? "border-callisto" : "border-slate-200 opacity-70"
      }`}
      data-testid={`team-user-card-${user.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-full bg-callisto-navy text-callisto-gold flex items-center justify-center text-sm font-bold shrink-0">
          {userInitials(user.full_name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-callisto-navy truncate">{user.full_name}</h3>
            <span
              className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 shrink-0 ${ROLE_BADGE_CLS[user.role] || "bg-slate-100 text-slate-700"}`}
            >
              {ROLE_LABEL[user.role] || user.role}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-callisto-muted truncate">{user.email}</p>
          {/* PHONE (RC-27/D1). The Team page had no phone field at all, which
              is why a Sales Manager looking for a rep mid-field had nowhere to
              look. The `tel:` link is guarded on the stored NORMAL FORM — ten
              digits, nothing else — because the defect being answered here is
              an email address that was rendered as a dialable link. This is a
              display guard, not a second validator: `backend/phone.py` is the
              validator, and it is what guarantees the shape trusted here. */}
          {user.phone ? (
            /^\d{10}$/.test(String(user.phone)) ? (
              <a
                href={`tel:+91${user.phone}`}
                className="mt-0.5 flex items-center gap-1 text-xs text-callisto-navy hover:text-callisto-gold"
                data-testid={`team-phone-${user.id}`}
              >
                <Phone size={12} /> {user.phone}
              </a>
            ) : (
              <p className="mt-0.5 flex items-center gap-1 text-xs text-amber-700"
                 data-testid={`team-phone-invalid-${user.id}`}>
                <Phone size={12} /> {user.phone} — not a valid mobile, not dialable
              </p>
            )
          ) : (
            <p className="mt-0.5 text-xs text-callisto-muted"
               data-testid={`team-phone-missing-${user.id}`}>
              No phone number on file
            </p>
          )}
          <div className="mt-1.5 text-[11px] text-callisto-body space-y-0.5">
            {/* ONE TERRITORY RENDERER (RC-10). Chips come from the derived
                `territory` label, which the server keeps in step with
                `territory_unit_ids`, and each chip is a <TerritoryLabel/> so
                the trimming and the empty case have one implementation.
                Splitting on the comma is only safe for a MIGRATED value —
                unit names are Bihar districts and the four Patna zones and
                carry no comma. An unmigrated value is free text that often
                does ("Patna, Head Office"), and splitting it produced two
                chips that looked exactly like real units. So it is never
                split: it renders whole, amber, with the needs-review sentence
                the component owns. */}
            {user.territory_unmigrated ? (
              <TerritoryLabel
                value={user.territory}
                unmigrated
                className="block"
                testId={`team-territory-review-${user.id}`}
              />
            ) : user.territory ? (
              <div className="flex flex-wrap gap-1" data-testid={`team-territories-${user.id}`}>
                {user.territory.split(",").map((t) => t.trim()).filter(Boolean).map((t) => (
                  <span key={t} className="rounded-full bg-callisto-navy/5 text-callisto-navy font-semibold px-2 py-0.5">
                    <TerritoryLabel value={t} />
                  </span>
                ))}
              </div>
            ) : FIELD_ROLES.includes(user.role) ? (
              // A field role with no unit sees no distributors and appears on
              // no territory list — a gap, shown as a gap. The office roles
              // carry no territory by design, so saying so on their card would
              // be noise rather than information.
              <TerritoryLabel value="" hint="visible on no territory list" testId={`team-territory-${user.id}`} />
            ) : null}
            {manager && <p>Reports to: <span className="font-semibold text-callisto-navy">{manager.full_name}</span></p>}
            {/* AN OFFICER WITH NO SALES MANAGER IS BROKEN, NOT MERELY FREE.
                This line used to read "Unassigned — available to induct",
                which describes a queue rather than a fault. Since #111 took
                the Tech Admin out of the approval chains, an officer with no
                manager has NO stage-1 approver at all: their expenses, leaves
                and tour plans reach nobody, which is why `waiting_on` returns
                an empty approver list for them. Release can no longer create
                one — it names the manager taking the officer over — so anybody
                still in this state is a legacy row, and the only useful thing
                the card can do is say so plainly and name the fix. */}
            {isUnassignedSo && !user.archived && (
              <p className="text-red-700 font-semibold" data-testid={`team-no-manager-${user.id}`}>
                No Sales Manager — their expenses, leaves and tour plans cannot
                be verified by anybody. Induct them into a team.
              </p>
            )}
            {pendingChange && (
              <p className="text-teal-700 font-semibold inline-flex items-center gap-1" data-testid={`team-pending-change-${user.id}`}>
                <Hourglass size={11} />
                {PENDING_LABEL[pendingChange.kind] || "Change"} pending approval
                {pendingChange.sales_officer_id === viewerId ? " (your request)" : ` (by ${pendingChange.sales_officer_name})`}
              </p>
            )}
            {pendingMaster && (
              <p className="text-teal-700 font-semibold inline-flex items-center gap-1" data-testid={`team-pending-master-${user.id}`}>
                <Hourglass size={11} />
                {pendingMasterLabel(pendingMaster)}
              </p>
            )}
            {!user.is_active && <p className="text-orange-600 font-semibold">Deactivated</p>}
            {user.archived && <p className="text-orange-700 font-semibold">Archived — departed</p>}
          </div>
        </div>
      </div>
      {user.archived ? (
        canAdminister && (
          <div className="mt-3 flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onRestore}
              className="h-8 flex-1 border-callisto text-callisto-navy text-[11px] font-semibold"
              data-testid={`team-restore-${user.id}`}
            >
              <RotateCcw size={12} className="mr-1" /> Restore
            </Button>
          </div>
        )
      ) : (
        <>
          {canAdminister && (
            <div className="mt-3 flex gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={onEdit}
                className="h-8 flex-1 border-callisto text-callisto-navy text-[11px] font-semibold"
                data-testid={`team-edit-${user.id}`}
              >
                <UserCog size={12} className="mr-1" /> Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onToggleActive}
                className={`h-8 flex-1 text-[11px] font-semibold ${
                  user.is_active ? "border-red-200 text-red-700 hover:bg-red-50" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                }`}
                data-testid={`team-toggle-active-${user.id}`}
              >
                {user.is_active ? "Deactivate" : "Reactivate"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onArchive}
                className="h-8 flex-1 border-orange-200 text-orange-700 hover:bg-orange-50 text-[11px] font-semibold"
                data-testid={`team-archive-${user.id}`}
              >
                <Archive size={12} className="mr-1" /> Archive
              </Button>
            </div>
          )}
          {showTeamActions && (
            <div className="mt-2 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onReassign}
                className="h-8 flex-1 border-callisto text-callisto-navy text-[11px] font-semibold"
                data-testid={`team-reassign-${user.id}`}
              >
                <ArrowRightLeft size={12} className="mr-1" /> Reassign
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onRelease}
                className="h-8 flex-1 border-amber-300 text-amber-800 hover:bg-amber-50 text-[11px] font-semibold"
                data-testid={`team-release-${user.id}`}
              >
                <UserMinus size={12} className="mr-1" /> Release
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
